import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Brain, ClipboardCheck, BookOpen, AlertTriangle, MessageCircle, GraduationCap, FileText, Phone, ChevronRight } from 'lucide-react';
import SEO from '../components/SEO';
import CrisisSupport from '../components/CrisisSupport';
import ScrollIndicator from '../components/ScrollIndicator';
import './Services.css';

const Services = () => {
  useEffect(() => {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) entry.target.classList.add('revealed');
      });
    }, { threshold: 0.1, rootMargin: '0px 0px -50px 0px' });
    const els = document.querySelectorAll('.scroll-reveal');
    els.forEach((el) => observer.observe(el));
    return () => els.forEach((el) => observer.unobserve(el));
  }, []);

  const assessments = [
    {
      icon: <Brain size={28} strokeWidth={1.5} />,
      title: 'Autism Assessment',
      brief: 'Assessments using recognised tools such as ADOS-2, MIGDAS or DASI, aligned with Australian Clinical Guidelines.',
      to: '/autism-assessment'
    },
    {
      icon: <ClipboardCheck size={28} strokeWidth={1.5} />,
      title: 'ADHD Assessment',
      brief: 'Comprehensive evaluations to understand attention, focus and how you or your child learns best.',
      to: '/adhd-assessment'
    },
    {
      icon: <AlertTriangle size={28} strokeWidth={1.5} />,
      title: 'FASD Assessment',
      brief: 'Neurodevelopmental assessments for children affected by prenatal alcohol exposure.',
      to: '/fasd-assessment'
    },
    {
      icon: <BookOpen size={28} strokeWidth={1.5} />,
      title: 'Learning Assessment',
      brief: 'Assessments for specific learning disorders in reading, writing and mathematics.',
      to: '/learning-assessment'
    }
  ];

  const otherServices = [
    {
      icon: <MessageCircle size={28} strokeWidth={1.5} />,
      title: 'Therapy',
      brief: 'Individual therapy for anxiety, depression, trauma and more.',
      to: '/therapy'
    },
    {
      icon: <GraduationCap size={28} strokeWidth={1.5} />,
      title: 'Schools',
      brief: 'School Wellbeing Hub partnership model for rural and regional schools.',
      to: '/schools'
    },
    {
      icon: <FileText size={28} strokeWidth={1.5} />,
      title: 'Information for Referrers',
      brief: 'For GPs, paediatricians and NDIS providers.',
      to: '/referrers'
    }
  ];

  return (
    <div className="page-services" data-testid="services-page">
      <SEO
        title="Psychology Services Coffs Harbour | Assessments & Therapy | Clinical Minds"
        description="Neurodevelopmental assessments and therapy in Coffs Harbour. Autism, ADHD, FASD and learning assessments. Individual therapy. School psychology. NDIS support. Clinical Psychologist (Registrar) Lachlan Beaton."
        canonicalUrl="https://www.clinicalminds.net/services"
      />
      <ScrollIndicator />

      <section id="main-content" className="interior-hero" data-testid="page-hero">
        <div className="container">
          <span className="section-label">Services</span>
          <h1 className="heading-hero">Our Services</h1>
          <p className="hero-subline">Neurodevelopmental assessment and psychology in Coffs Harbour</p>
        </div>
      </section>

      {/* Assessments */}
      <section className="svc-overview-section scroll-reveal" data-testid="svc-assessments">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">Assessments</h2>
            <p className="body-large">Our assessment services</p>
          </div>
          <div className="svc-link-grid">
            {assessments.map((item, i) => (
              <Link to={item.to} key={i} className="svc-link-card" data-testid={`svc-assess-${i}`}>
                <div className="svc-link-icon">{item.icon}</div>
                <div className="svc-link-text">
                  <h3 className="heading-3">{item.title}</h3>
                  <p className="body-medium">{item.brief}</p>
                </div>
                <ChevronRight size={20} className="svc-link-arrow" />
              </Link>
            ))}
          </div>
          <div className="svc-journey-link">
            <Link to="/what-to-expect" className="assess-link">
              What to expect from the assessment process →
            </Link>
          </div>
        </div>
      </section>

      {/* Other services */}
      <section className="svc-overview-section svc-alt scroll-reveal" data-testid="svc-other">
        <div className="container">
          <div className="section-header">
            <h2 className="heading-1">More services</h2>
          </div>
          <div className="svc-link-grid svc-link-grid-3">
            {otherServices.map((item, i) => (
              <Link to={item.to} key={i} className="svc-link-card" data-testid={`svc-other-${i}`}>
                <div className="svc-link-icon">{item.icon}</div>
                <div className="svc-link-text">
                  <h3 className="heading-3">{item.title}</h3>
                  <p className="body-medium">{item.brief}</p>
                </div>
                <ChevronRight size={20} className="svc-link-arrow" />
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Fees + CTA */}
      <section className="svc-cta-section">
        <div className="container">
          <div className="svc-cta-card">
            <h2 className="heading-2">Fees and rebates</h2>
            <p className="body-large">Medicare rebates, NDIS funding and private fees available.</p>
            <div className="svc-cta-buttons">
              <Link to="/fees" className="btn-secondary" data-testid="svc-fees-btn">View Fees</Link>
              <Link to="/contact" className="btn-primary" data-testid="svc-contact-btn">Contact Us</Link>
              <a href="tel:0266002710" className="assess-phone-cta">
                <Phone size={18} /> 02 6600 2710
              </a>
            </div>
          </div>
        </div>
      </section>

      <CrisisSupport />
    </div>
  );
};

export default Services;
